import React, { useEffect, useMemo } from 'react';
import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

export const extractImageUrls = (html = '') => {
  const matches = Array.from(html.matchAll(/<img[^>]+src=["']([^"']+)["']/gi));
  return [...new Set(matches.map((match) => match[1]).filter(Boolean))];
};

export const getRepresentativeImageUrl = (content = '', selectedUrl = '', fallbackUrl = '') => {
  const imageUrls = extractImageUrls(content);

  if (selectedUrl && imageUrls.includes(selectedUrl)) {
    return selectedUrl;
  }

  return imageUrls[0] || fallbackUrl || '';
};

const RepresentativeImageSelector = ({
  content,
  value,
  onChange,
  autoSelect = true,
  disabled = false,
}) => {
  const theme = useTheme();
  const imageUrls = useMemo(() => extractImageUrls(content), [content]);

  useEffect(() => {
    if (!autoSelect || disabled) return;
    if (imageUrls.length === 0) {
      if (value) onChange('');
      return;
    }

    if (!value || !imageUrls.includes(value)) {
      onChange(imageUrls[0]);
    }
  }, [imageUrls, onChange, value, autoSelect, disabled]);

  if (imageUrls.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.25} sx={{ mt: 1.5 }}>
      <Typography variant="body4" color={theme.palette.black[6]}>
        본문 이미지 중 대표로 사용할 이미지를 선택하세요.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(3, 1fr)',
            sm: 'repeat(4, 1fr)',
            md: 'repeat(5, 1fr)',
          },
          gap: 1,
        }}
      >
        {imageUrls.map((imageUrl) => {
          const isSelected = imageUrl === value;

          return (
            <ButtonBase
              key={imageUrl}
              disabled={disabled}
              onClick={() => onChange(imageUrl)}
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                border: `1px solid ${isSelected ? theme.palette.black[7] : theme.palette.grey.line}`,
                overflow: 'hidden',
                backgroundColor: theme.palette.grey.bg,
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt="대표 이미지 후보"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                sx={{
                  position: 'absolute',
                  top: 6,
                  left: 6,
                  height: 22,
                  px: 0.75,
                  backgroundColor: isSelected
                    ? theme.palette.black[7]
                    : 'rgba(255, 255, 255, 0.88)',
                  color: isSelected ? (theme.palette.mode === 'dark' ? '#111' : '#fff') : '#555',
                  border: `1px solid ${isSelected ? theme.palette.black[7] : theme.palette.grey[300]}`,
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {isSelected && <CheckIcon sx={{ fontSize: 13 }} />}
                <Box component="span">대표</Box>
              </Stack>
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
};

export default RepresentativeImageSelector;
